import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { apiFetchFile } from './api';

/**
 * PERİYODİK KDV BEYANI — panel istemcisi.
 *
 * ⚠ BUGÜN YALNIZ ÖNİZLEME VAR. Beyanın kaydedilmesi — arşivlenen XML, gönderim
 * durumu, imzalı makbuz — ayrı bir adım ve henüz yazılmadı. Panelde "gönder"
 * düğmesi YOK, çünkü olmayan bir yeteneği düğmeyle vaat etmek, operatörün
 * beyanı verdiğini sanması demektir.
 */

export interface DeclarationProblem {
  level: 'BLOCKER' | 'WARNING';
  code: string;
  grid?: string;
  amount?: string;
  reverseCharge?: string;
  inputBase?: string;
  baseGrids?: string;
}

export interface VatReturnPreview {
  period: {
    key: string;
    year: number;
    periodicity: 'MONTHLY' | 'QUARTERLY';
    periodNumber: number;
    startDate: string;
    endDate: string;
    dueDate: string;
  };
  documentCounts: { sales: number; purchases: number };
  declaration: {
    grids: Array<{ grid: string; amount: string }>;
    totalDue: string;
    totalDeductible: string;
    grid71: string | null;
    grid72: string | null;
    problems: DeclarationProblem[];
  };
  submittable: boolean;
}

export function useVatReturnPreview(period: string | null) {
  return useQuery({
    queryKey: ['vat-return', period],
    enabled: period !== null && /^\d{4}(Q[1-4]|M(0[1-9]|1[0-2]))$/.test(period ?? ''),
    queryFn: () => api.get<VatReturnPreview>(`/vat-return/prepare?period=${String(period)}`),
  });
}

/**
 * Kutu açıklamaları — Kader başlıklarıyla.
 *
 * ⚠ AÇIKLAMALAR SÜSLEME DEĞİL. Muhasebecisi olmayan bir operatör "kutu 88" ne
 * demek bilmez; kutuyu adıyla göstermek, yanlış bir rakamı fark etmenin tek
 * yoludur. Bu tablo `resolve-vat-treatment.ts` ve `purchase-vat-math.ts`in
 * ürettiği kutuların TAMAMINI kapsamalı.
 */
export const GRID_LABELS: Record<string, string> = {
  '0': 'Özel rejim matrahı',
  '1': '%6 matrahı',
  '2': '%12 matrahı',
  '3': '%21 matrahı — yurtiçi satışların ana kutusu',
  '44': 'AB B2B hizmet (ters yükümlülük) — IC listing kodu S',
  '45': 'Yurtiçi ters yükümlülük, satıcı sıfatıyla',
  '46': 'AB’ye mal teslimi — IC listing kodu L',
  '47': 'Diğer muaf çıkışlar: ihracat, AB dışı hizmet, OSS',
  '48': 'Kesilen kredi notları — yalnız 44 ve 46’ya ilişkin',
  '49': 'Kesilen kredi notları — diğer çıkışlara ilişkin',
  '54': 'Hesaplanan KDV (kutu 1+2+3 üzerinden)',
  '55': 'Borçlanılan KDV — AB alımları (kutu 86 ve 88)',
  '56': 'Borçlanılan KDV — diğer ters yükümlülükler (kutu 87)',
  '57': 'İthalatta ertelenen KDV',
  '59': 'İndirilecek KDV',
  '61': 'Düzeltme — Hazine lehine',
  '62': 'Düzeltme — mükellef lehine',
  '63': 'Alınan kredi notları üzerindeki iade edilecek KDV',
  '64': 'Kesilen kredi notları üzerindeki geri alınacak KDV',
  '71': 'ÖDENECEK',
  '72': 'İADE ALINACAK',
  '81': 'Yeniden satılacak mal ve hammadde',
  '82': 'Hizmet ve çeşitli mal alımları — ana gider kutusu',
  '83': 'Yatırım (duran varlık) alımları',
  '84': 'Alınan kredi notları — yalnız 86 ve 88’e ilişkin',
  '85': 'Alınan kredi notları — 81/82/83’e ilişkin',
  '86': 'AB’den mal alımı',
  '87': 'Diğer ters yükümlülüklü girişler',
  '88': 'AB’den hizmet alımı',
};

/**
 * Intervat XML indirir.
 *
 * ⚠ TARAYICININ KENDİ İNDİRMESİ KULLANILAMAZ: panel token'ı localStorage'da
 * ve `<a href>` isteğine `Authorization` başlığı eklenemez — bağlantı 401
 * döner ve operatör sebebini hiçbir yerde göremez. Baytlar bu yüzden burada
 * çekiliyor (`apiFetchFile`, e-fatura XML indirmesiyle aynı yol).
 *
 * ⚠ DOSYA ADI SUNUCUNUNDUR. İstemcide ikinci bir ad üretmek, portala
 * yüklenen dosyanın adıyla kayıtlarımızdaki adı ayrıştırırdı.
 */
export async function downloadVatReturnXml(
  period: string,
  clientListingNihil: boolean,
): Promise<{ blob: Blob; fileName: string }> {
  const qs = new URLSearchParams({ period, clientListingNihil: String(clientListingNihil) });
  const file = await apiFetchFile(`/vat-return/xml?${qs.toString()}`);
  return { blob: file.blob, fileName: file.fileName ?? `TVA-${period}.xml` };
}

// ────────────────────────────────────── AB içi işlemler listesi (723)

/**
 * ⚠ LİSTE MÜŞTERİ BAZINDADIR, belge bazında değil: aynı müşteriye kesilen üç
 * fatura TEK satır olur. `clientsNbr` ve `amountSum` şemada ZORUNLU
 * özniteliklerdir ve içerikle tutmak zorundadır.
 */
export interface IcListingClient {
  countryCode: string;
  /** ⚠ ÜLKE ÖN EKİ OLMADAN — ülke ayrı bir öznitelikte. */
  vatNumber: string;
  code: 'L' | 'S' | 'T';
  amount: string;
}

export interface IcListingPreview {
  period: { key: string; year: number; periodNumber: number };
  listing: {
    clients: IcListingClient[];
    clientsNbr: number;
    amountSum: string;
    problems: Array<{ code: string; documentNumber?: string; grid?: string; vatNumber?: string }>;
  };
  submittable: boolean;
}

export function useIcListing(period: string | null) {
  return useQuery({
    queryKey: ['ic-listing', period],
    enabled: period !== null && /^d{4}(Q[1-4]|M(0[1-9]|1[0-2]))$/.test(period ?? ''),
    queryFn: () => api.get<IcListingPreview>(`/vat-return/ic-listing?period=${String(period)}`),
  });
}

export async function downloadIcListingXml(
  period: string,
): Promise<{ blob: Blob; fileName: string }> {
  const file = await apiFetchFile(`/vat-return/ic-listing/xml?period=${period}`);
  return { blob: file.blob, fileName: file.fileName ?? `ICO-${period}.xml` };
}

/** IC listing kodları — operatörün diliyle. */
export const IC_CODE_LABELS: Record<string, string> = {
  S: 'Hizmet (kutu 44) — AB B2B, ters yükümlülük',
  L: 'Mal teslimi (kutu 46)',
  T: 'Üçgen işlem',
};

// ─────────────────────────────────── yıllık müşteri listesi (725)

/**
 * ⚠⚠ `issuedBy` BURADA HER ZAMAN "BE" — 723ün TAM TERSİ. IC listesinde şema
 * Belçikayı REDDEDER, burada yalnız Belçikayı kabul eder.
 */
export interface ClientListingClient {
  vatNumber: string;
  turnOver: string;
  vatAmount: string;
  /** ⚠ BİLGİ AMAÇLI — süzgeç DEĞİL. Rakamın metni doğrulanmadı. */
  belowCommonThreshold: boolean;
}

export interface ClientListingPreview {
  year: number;
  listing: {
    clients: ClientListingClient[];
    clientsNbr: number;
    turnOverSum: string;
    vatAmountSum: string;
    problems: Array<{ code: string; documentNumber?: string; vatNumber?: string }>;
  };
  submittable: boolean;
}

export function useClientListing(year: number | null) {
  return useQuery({
    queryKey: ['client-listing', year],
    enabled: year !== null,
    queryFn: () => api.get<ClientListingPreview>(`/vat-return/client-listing?year=${String(year)}`),
  });
}

export async function downloadClientListingXml(
  year: number,
): Promise<{ blob: Blob; fileName: string }> {
  const file = await apiFetchFile(`/vat-return/client-listing/xml?year=${String(year)}`);
  return { blob: file.blob, fileName: file.fileName ?? `LK-${String(year)}.xml` };
}

// ─────────────────────────────────── beyan kayıtları (M5e)

/**
 * ⚠ ÖNİZLEME İLE KAYIT AYRI ŞEYLER. Yukarıdaki `use*Preview` kancaları her
 * çağrıda kutuları YENİDEN toplar ve hiçbir şey saklamaz; buradakiler ise
 * dondurulmuş kayıtları okur. Bir kural değiştiği gün ikisi AYRIŞIR — ve o gün
 * doğru olan, gönderilmiş olandır.
 */
export type VatDeclarationType = 'PERIODIC' | 'IC_LISTING' | 'CLIENT_LISTING';
export type VatDeclarationStatus = 'PREPARED' | 'SUBMITTED' | 'REPLACED';

export interface VatDeclarationRecord {
  id: string;
  declarationType: VatDeclarationType;
  periodKey: string;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  status: VatDeclarationStatus;
  totalDue: string | null;
  totalDeductible: string | null;
  fileName: string;
  xmlSha256: string;
  xmlByteLength: number;
  preparedAt: string;
  submittedAt: string | null;
  submissionRef: string | null;
  receiptStoragePath: string | null;
  replacesDeclarationId: string | null;
  replacedByDeclarationId: string | null;
  /**
   * ⚠⚠ KOLON DEĞİL, ÖDEME SATIRLARINDAN TÜRETİLİR (M12). Beyanı GÖNDERMEK
   * KDV borcunu SIFIRLAMAZ — `submittedAt` "beyan edildi" der, bu alanlar
   * "ne kadarı ödendi" der ve ayrı bir olgudur. Yalnız periyodik beyanda
   * (`totalDue` doluyken) anlamlı; listelerde `remainingAmount` her zaman
   * `null`.
   */
  paidAmount: string;
  /**
   * DEVLETE ÖDENECEK tutar — kutu 71 (72 doluysa 0). ⚠ `totalDue` DEĞİL:
   * o, indirilecek KDV düşülmeden önceki brüt toplamdır.
   */
  payableAmount: string | null;
  remainingAmount: string | null;
  /** Ödenecekten fazla ödenen (ör. düzeltici beyan borcu düşürdüğünde). */
  overpaidAmount: string | null;
}

export interface VatDeclarationPaymentRecord {
  id: string;
  vatDeclarationId: string;
  paidOn: string;
  amount: string;
  reference: string | null;
  note: string | null;
  createdAt: string;
  /** Ödemenin atılmış yevmiye fişi; `null` → deftere henüz girmedi (F5). */
  journalEntryNumber: string | null;
}

export const VAT_DECLARATION_TYPE_LABELS: Record<VatDeclarationType, string> = {
  PERIODIC: 'Periyodik KDV beyanı',
  IC_LISTING: 'AB içi işlemler listesi (723)',
  CLIENT_LISTING: 'Yıllık müşteri listesi (725)',
};

/**
 * ⚠ DURUM METİNLERİ İYİMSER OLMAMALI. `PREPARED`, "beyan verildi" DEĞİL
 * "dosya hazır" demektir; ikisini aynı renkte göstermek, operatörün süresi
 * dolmakta olan bir beyanı verdiğini sanmasına yol açar.
 */
export const VAT_DECLARATION_STATUS_LABELS: Record<VatDeclarationStatus, string> = {
  PREPARED: 'Hazırlandı — HENÜZ GÖNDERİLMEDİ',
  SUBMITTED: 'Gönderildi (makbuz arşivde)',
  REPLACED: 'Düzeltmeyle değiştirildi',
};

export function useVatDeclarations() {
  return useQuery({
    queryKey: ['vat-declarations'],
    queryFn: () => api.get<VatDeclarationRecord[]>('/vat-declarations'),
  });
}

export function useRecordVatDeclaration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      type: VatDeclarationType;
      periodKey: string;
      clientListingNihil?: boolean;
      /** Düzeltici beyan: düzeltilen GÖNDERİLMİŞ kaydın kimliği. */
      replacesDeclarationId?: string;
      /** Düzeltilen beyanın Intervat makbuzundaki referans (kayıtlı değilse). */
      replacedDeclarationReference?: string;
    }) => api.post<VatDeclarationRecord>('/vat-declarations', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vat-declarations'] }),
  });
}

/**
 * ⚠ MAKBUZ HAM BAYT OLARAK GİDER (`application/pdf`), multipart değil —
 * sunucu tarafındaki gerekçeyle aynı: multipart bir ayrıştırıcı bağımlılığı ve
 * kullanıcı girdisinden gelen bir dosya adı getirirdi.
 */
export function useUploadVatReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; file: File; submissionRef?: string }) => {
      const bytes = await input.file.arrayBuffer();
      return api.postRaw<VatDeclarationRecord>(
        `/vat-declarations/${input.id}/receipt`,
        bytes,
        'application/pdf',
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vat-declarations'] }),
  });
}

export function useDiscardVatDeclaration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/vat-declarations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vat-declarations'] }),
  });
}

// ─────────────────────────────────────── KDV'nin devlete fiilen ödenmesi (M12)

/**
 * ⚠⚠ BEYANIN GÖNDERİLMESİYLE (`useUploadVatReceipt`) KARIŞTIRILMAZ: o "beyan
 * edildi" der, bu "devlete para gönderildi" der — ayrı olgular, ayrı
 * tarihler. Yalnız GÖNDERİLMİŞ periyodik beyanda anlamlıdır.
 */
export function useVatPayments(declarationId: string | null) {
  return useQuery({
    queryKey: ['vat-declarations', declarationId, 'payments'],
    queryFn: () =>
      api.get<VatDeclarationPaymentRecord[]>(`/vat-declarations/${declarationId}/payments`),
    enabled: declarationId !== null,
  });
}

export function useRecordVatPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      declarationId: string;
      amount: string;
      paidOn: string;
      reference?: string;
      note?: string;
    }) =>
      api.post<VatDeclarationRecord>(`/vat-declarations/${input.declarationId}/payments`, {
        amount: input.amount,
        paidOn: input.paidOn,
        reference: input.reference,
        note: input.note,
      }),
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: ['vat-declarations'] });
      void qc.invalidateQueries({
        queryKey: ['vat-declarations', vars.declarationId, 'payments'],
      });
    },
  });
}

export function useRemoveVatPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { declarationId: string; paymentId: string }) =>
      api.delete<void>(`/vat-declarations/${input.declarationId}/payments/${input.paymentId}`),
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: ['vat-declarations'] });
      void qc.invalidateQueries({
        queryKey: ['vat-declarations', vars.declarationId, 'payments'],
      });
    },
  });
}

/** ⚠ ARŞİVDEN okur: üretilen değil, GÖNDERİLEN baytlar. */
export async function downloadArchivedXml(
  id: string,
  fallbackName: string,
): Promise<{ blob: Blob; fileName: string }> {
  const file = await apiFetchFile(`/vat-declarations/${id}/xml`);
  return { blob: file.blob, fileName: file.fileName ?? fallbackName };
}

export async function downloadReceipt(id: string): Promise<{ blob: Blob; fileName: string }> {
  const file = await apiFetchFile(`/vat-declarations/${id}/receipt`);
  return { blob: file.blob, fileName: file.fileName ?? 'makbuz.pdf' };
}

// ─────────────────────────────────── kutu 61/62 düzeltmeleri (M5f)

/**
 * ⚠ 61 HAZİNE LEHİNE, 62 MÜKELLEF LEHİNE. İkisi de POZİTİF; işaret kutudan
 * gelir. "Eksi 61" diye bir şey yoktur.
 */
export type VatAdjustmentGrid = '61' | '62';

export const VAT_ADJUSTMENT_REASONS = [
  'MATERIAL_ERROR',
  'BAD_DEBT',
  'ASSET_REVISION',
  'MIXED_USE_REVISION',
  'PRIVATE_USE',
  'OTHER',
] as const;
export type VatAdjustmentReason = (typeof VAT_ADJUSTMENT_REASONS)[number];

export const VAT_ADJUSTMENT_REASON_LABELS: Record<VatAdjustmentReason, string> = {
  MATERIAL_ERROR: 'Maddi hata — geçmiş dönemde yanlış beyan',
  /** ⚠ Batık alacak KUTU 62'ye yazılır; CİRO AZALTILMAZ. */
  BAD_DEBT: 'Batık alacak (ciro azaltılmaz, KDV 62’ye)',
  ASSET_REVISION: 'Yatırım malı revizyonu (1/5 ya da 1/15)',
  MIXED_USE_REVISION: 'Karma kullanım oranının kesinleşmesi',
  PRIVATE_USE: 'Özel kullanım payı',
  OTHER: 'Diğer',
};

export interface VatAdjustmentRecord {
  id: string;
  targetGrid: VatAdjustmentGrid;
  amount: string;
  reason: VatAdjustmentReason;
  note: string;
  originPeriodKey: string;
  appliedInPeriodKey: string;
  counterAccountCode: string;
  journalEntryId: string;
  status: 'ACTIVE' | 'CANCELLED';
  cancelledByEntryId: string | null;
  cancelledAt: string | null;
  createdAt: string;
}

export function useVatAdjustments(appliedInPeriodKey: string) {
  return useQuery({
    queryKey: ['vat-adjustments', appliedInPeriodKey],
    queryFn: () =>
      api.get<VatAdjustmentRecord[]>(
        `/vat-adjustments?appliedInPeriodKey=${encodeURIComponent(appliedInPeriodKey)}`,
      ),
  });
}

/**
 * ⚠ BU MUTASYON BİR YEVMİYE FİŞİ DE ATAR. Düzeltme deftere yazılmadan var
 * olamaz; "KDV mutabakatı" raporu aksi hâlde yapısal olarak fark gösterirdi.
 */
export function useCreateVatAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      targetGrid: VatAdjustmentGrid;
      amount: string;
      reason: VatAdjustmentReason;
      note: string;
      originPeriodKey: string;
      appliedInPeriodKey: string;
      counterAccountCode: string;
      entryDate: string;
    }) => api.post<VatAdjustmentRecord>('/vat-adjustments', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vat-adjustments'] });
      // ⚠ Beyan önizlemesi de tazelenmeli: kutular değişti.
      void qc.invalidateQueries({ queryKey: ['vat-return'] });
    },
  });
}

/** ⚠ İPTAL TERS KAYITTIR, SİLME DEĞİL — silme ucu hiç yok. */
export function useCancelVatAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; entryDate: string }) =>
      api.post<VatAdjustmentRecord>(`/vat-adjustments/${input.id}/cancel`, {
        entryDate: input.entryDate,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vat-adjustments'] });
      void qc.invalidateQueries({ queryKey: ['vat-return'] });
    },
  });
}

/**
 * DÖNEM LİSTESİ — "hangi dönemi hazırlamalıyım".
 *
 * ⚠⚠ BU HESAP PANELDE YAPILIYORDU VE YANLIŞTI: ekran bugünün çeyreğini
 * açıyordu, oysa beyanı verilecek olan SON BİTEN çeyrektir — operatör bu ekranı
 * her zaman bir sonraki dönemin içindeyken açar. Üstelik panel çeyrekleri koda
 * gömüyordu; aylık mükellefe çeyrek anahtarı teklif ediliyordu. Periyodiklik
 * bir AYARDIR: yalnız sunucu bilir.
 */
export interface VatPeriodOverview {
  periodicity: 'QUARTERLY' | 'MONTHLY';
  today: string;
  /** İçinde bulunulan (henüz BİTMEMİŞ) dönem. */
  current: string;
  /** Beyanı verilecek olan (son biten) dönem — ekranın varsayılanı. */
  due: string;
  items: Array<{
    key: string;
    startDate: string;
    endDate: string;
    dueDate: string;
    ended: boolean;
    /** `PREPARED` · `SUBMITTED` · `null` (hiç kaydedilmemiş). */
    declarationStatus: string | null;
  }>;
}

export function useVatPeriods() {
  return useQuery({
    queryKey: ['vat-return', 'periods'],
    queryFn: () => api.get<VatPeriodOverview>('/vat-return/periods'),
  });
}
